import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Typography, Paper, Table, TableBody, TextField, DialogActions, DialogTitle,
  Dialog, DialogContent, TableCell, TableContainer, TableHead, TableRow,
  Button, Toolbar, Box, IconButton, FormControl, InputLabel, Grid, Tooltip
} from '@mui/material';
import styled, { keyframes, css } from 'styled-components';
import '../../styles/HiringdashBoard.css';
import CandidateData from './CandidateData.js';
import Pagination from '../../utils/Pagination.js';
import { countryOptions } from '../shared/dropdown_values';
import { Select, MenuItem } from "@mui/material";
import NotificationDialog from './NotificationDialog';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import { Edit as EditIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import * as XLSX from 'xlsx';
import useActivityLogger from '../../hooks/useActivityLogger';
import CircularProgress from '@mui/material/CircularProgress';
import { ThemeProvider } from '@mui/material/styles';
import recruiterTheme, { FONT_BODY, FONT_DISPLAY, COLORS, TYPE, RADIUS, PAGE_PADDING, ELLIPSIS_CSS } from '../../styles/recruiterTheme';


// ─── Animations ───────────────────────────────────────────────────────────────
const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50%       { transform: scale(1.04); }
`;

// ─── Styled Components ────────────────────────────────────────────────────────
// No app bar is rendered above this page, so the top gap is just the page padding.
// Inside a dashboard (onBack passed) the dashboard supplies the 32px canvas padding.
const PageWrapper = styled.div`
  font-family: ${FONT_BODY};
  width: 100%;
  box-sizing: border-box;
  ${p => p.$embedded ? '' : `
    min-height: 100vh;
    background: ${COLORS.background};
    padding: ${PAGE_PADDING}px;
  `}
`;

// Header block: back + title/subtitle on the left, primary actions on the right.
const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 24px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const BackBtn = styled(IconButton)`
  && {
    background: linear-gradient(135deg, #F3EEF9, #E8DEF8);
    border-radius: ${RADIUS.sm}px;
    width: 36px; height: 36px;
    flex-shrink: 0;
    transition: background 0.2s;
    &:hover { background: linear-gradient(135deg, #E8DEF8, #D0BCFF); }
    svg { color: #4F378B; font-size: 20px; }
  }
`;

const PageTitle = styled(Typography)`
  && {
    font-family: ${FONT_DISPLAY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.page}px !important;
    line-height: 1.25 !important;
    letter-spacing: -0.02em;
    background: linear-gradient(135deg, #2D1B5E 0%, #6750A4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const PageSubtitle = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.body}px !important;
    line-height: 1.5 !important;
    color: ${COLORS.muted} !important;
    font-weight: 500 !important;
    margin-top: 4px !important;
    ${ELLIPSIS_CSS}
  }
`;

// ─── Stats Row ────────────────────────────────────────────────────────────────
const StatsRow = styled.div`
  display: grid;
  /* auto-fit fills the row; 160px min keeps all 6 cards on one row at ~1080px content width */
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
  animation: ${fadeSlideIn} 0.45s ease both;
`;

const StatCard = styled.div`
  background: ${p => p.gradient || 'linear-gradient(135deg, #6750A4, #4F378B)'};
  border-radius: ${RADIUS.md}px;
  padding: 16px 24px;
  min-height: 88px;
  height: 100%;
  box-sizing: border-box;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: #fff;
  position: relative;
  overflow: hidden;
  box-shadow: 0 6px 20px ${p => p.shadow || 'rgba(103,80,164,0.26)'};
  transition: box-shadow 0.22s ease;
  cursor: default;
  &:hover { box-shadow: 0 12px 28px ${p => p.shadow || 'rgba(103,80,164,0.36)'}; }
  &::after {
    content: '';
    position: absolute;
    top: -28px; right: -28px;
    width: 80px; height: 80px;
    border-radius: 50%;
    background: rgba(255,255,255,0.1);
  }
`;

const StatNum = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.stat}px;
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  margin-bottom: 4px;
`;

const StatLbl = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  letter-spacing: 0.06em;
  opacity: 0.88;
  text-transform: uppercase;
`;

const StatIconWrap = styled.div`
  position: absolute;
  top: 50%; right: 16px;
  transform: translateY(-50%);
  display: flex;
  opacity: 0.2;
  svg { font-size: 40px; }
`;

// ─── Score Distribution Bar (currently not rendered) ─────────────────────────
const ScoreBarWrap = styled.div`
  margin-bottom: 24px;
  animation: ${fadeSlideIn} 0.5s ease 0.1s both;
`;

const ScoreBarCard = styled.div`
  background: #fff;
  border-radius: ${RADIUS.md}px;
  padding: 16px 24px;
  box-shadow: 0 2px 16px rgba(79,55,139,0.07);
  border: 1px solid ${COLORS.border};
`;

const ScoreBarLabel = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.small}px !important;
    color: #2D1B5E;
    margin-bottom: 12px !important;
  }
`;

const SegBar = styled.div`
  display: flex;
  height: 8px;
  border-radius: ${RADIUS.pill}px;
  overflow: hidden;
  background: #F0EAF9;
  gap: 2px;
`;

const SegFill = styled.div`
  height: 100%;
  background: ${p => p.color};
  width: ${p => p.pct}%;
  transition: width 0.6s cubic-bezier(.4,0,.2,1);
  border-radius: ${RADIUS.pill}px;
`;

const SegLegendRow = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 8px;
  flex-wrap: wrap;
`;

const SegLegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.caption}px;
  font-weight: 600;
  color: #49454F;
`;

const SegDot = styled.div`
  width: 8px; height: 8px;
  border-radius: 50%;
  background: ${p => p.color};
`;

// ─── Controls Bar ─────────────────────────────────────────────────────────────
const ControlsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`;

const SearchField = styled(TextField)`
  && {
    .MuiOutlinedInput-root {
      border-radius: ${RADIUS.sm}px;
      font-family: ${FONT_BODY};
      font-size: ${TYPE.body}px;
      height: 40px;
      background: #fff;
      &:hover fieldset { border-color: #7C5CBF; }
      &.Mui-focused fieldset { border-color: #6750A4; }
    }
    .MuiInputLabel-root { font-family: ${FONT_BODY}; font-size: ${TYPE.body}px; }
    width: 300px;
    max-width: 100%;
  }
`;

const ResultPill = styled.div`
  display: inline-flex;
  align-items: center;
  height: 24px;
  background: #EDE7F6;
  color: #4a3380;
  border-radius: ${RADIUS.pill}px;
  padding: 0 12px;
  font-size: ${TYPE.caption}px;
  font-weight: 700;
  font-family: ${FONT_BODY};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const SortHint = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${COLORS.muted};
  font-family: ${FONT_BODY};
  font-size: ${TYPE.caption}px;
  font-style: italic;
`;

// ─── Buttons ──────────────────────────────────────────────────────────────────
const BUTTON_BASE = `
  color: #fff !important;
  font-family: ${FONT_BODY} !important;
  font-weight: 600 !important;
  font-size: ${TYPE.small}px !important;
  border-radius: ${RADIUS.sm}px !important;
  height: 36px;
  padding: 0 16px !important;
  text-transform: none !important;
  white-space: nowrap;
  transition: box-shadow 0.2s, transform 0.2s !important;
  &:hover { transform: translateY(-1px); }
`;

const ConfigBtn = styled(Button)`
  && {
    ${BUTTON_BASE}
    background: linear-gradient(135deg, #6750A4, #4F378B) !important;
    box-shadow: 0 4px 14px rgba(79,55,139,0.28) !important;
    &:hover { box-shadow: 0 7px 20px rgba(79,55,139,0.38) !important; }
  }
`;

const ExportBtn = styled(Button)`
  && {
    ${BUTTON_BASE}
    background: linear-gradient(135deg, #2E7D32, #1B5E20) !important;
    box-shadow: 0 4px 14px rgba(46,125,50,0.28) !important;
    &:hover { box-shadow: 0 7px 20px rgba(46,125,50,0.38) !important; }
  }
`;

// Dialog building blocks (shared by the Configure and Edit dialogs).
const dialogPaperSx = { borderRadius: `${RADIUS.lg}px` };
const dialogTitleSx = {
  fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: TYPE.section, lineHeight: 1.25,
  color: '#2D1B5E', padding: '24px 24px 16px',
};
const dialogContentSx = { padding: '24px !important' };
const dialogActionsSx = {
  padding: '16px 24px', gap: '12px', justifyContent: 'flex-end',
  '& > :not(:first-of-type)': { marginLeft: 0 },
};
const dialogBtnSx = {
  height: 36, px: 2, borderRadius: `${RADIUS.sm}px`, fontFamily: FONT_BODY,
  fontWeight: 600, fontSize: TYPE.small, textTransform: 'none',
};
const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: `${RADIUS.sm}px` } };

// ─── Pending Badge ────────────────────────────────────────────────────────────
const PendingBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  background: linear-gradient(135deg, #FFF3E0, #FFE0B2);
  border: 1.5px solid #FFB74D;
  border-radius: ${RADIUS.sm}px;
  padding: 0 16px;
  box-shadow: 0 3px 10px rgba(255,152,0,0.18);
`;

const PendingNum = styled.span`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.section}px;
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: #E65100;
  ${css`animation: ${pulse} 2.5s ease-in-out infinite;`}
`;

// ─── Table ────────────────────────────────────────────────────────────────────
const StyledTableWrap = styled.div`
  animation: ${fadeSlideIn} 0.4s ease 0.05s both;
`;

const TableCard = styled(Paper)`
  && {
    border-radius: ${RADIUS.md}px !important;
    overflow: hidden;
    box-shadow: 0 4px 24px rgba(79,55,139,0.09) !important;
    border: 1px solid ${COLORS.border};
  }
`;

const HEAD_CELL_BASE = `
  color: #fff !important;
  font-family: ${FONT_BODY} !important;
  font-weight: 700 !important;
  font-size: ${TYPE.overline}px !important;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 12px 16px !important;
  white-space: nowrap;
  vertical-align: middle;
`;

const HeadCell = styled(TableCell)`
  && {
    ${HEAD_CELL_BASE}
    background: linear-gradient(135deg, #4F378B 0%, #6750A4 100%);
  }
`;

const SortableHeadCell = styled(TableCell)`
  && {
    ${HEAD_CELL_BASE}
    background: ${p => p.active ? '#3D2872' : 'linear-gradient(135deg, #4F378B 0%, #6750A4 100%)'};
    cursor: pointer;
    user-select: none;
    border-bottom: ${p => p.active ? '3px solid #D0BCFF' : '3px solid transparent'} !important;
    transition: background 0.2s;
    &:hover { background: #5a439c !important; }
  }
`;

const BodyRow = styled(TableRow)`
  && {
    transition: background 0.15s;
    &:nth-child(even) td { background: #FDFBFF; }
    &:hover td { background: #F3EEF9 !important; }
  }
`;

const BodyCell = styled(TableCell)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.small}px !important;
    font-weight: 500 !important;
    color: ${COLORS.ink} !important;
    padding: 12px 16px !important;
    vertical-align: middle;
    border-bottom: 1px solid #F0EAF9 !important;
  }
`;

// Name over email, both truncated so long values never widen the table.
const NameStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-width: 260px;
  min-width: 0;
`;

const NameLine = styled.span`
  font-weight: 600;
  font-size: ${TYPE.small}px;
  color: ${COLORS.ink};
  line-height: 1.5;
  ${ELLIPSIS_CSS}
`;

const EmailLine = styled.span`
  font-weight: 500;
  font-size: ${TYPE.caption}px;
  color: #6B5B9A;
  line-height: 1.5;
  ${ELLIPSIS_CSS}
`;

const CandidateIdBtn = styled(Button)`
  && {
    color: #6750A4 !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.small}px !important;
    font-variant-numeric: tabular-nums;
    text-transform: none !important;
    text-decoration: underline !important;
    text-underline-offset: 3px !important;
    height: 32px;
    min-width: 0;
    padding: 0 8px !important;
    margin-left: -8px;
    border-radius: ${RADIUS.sm}px !important;
    white-space: nowrap;
    &:hover { background: rgba(103,80,164,0.07) !important; }
  }
`;

const ResumeBtn = styled(Button)`
  && {
    color: #6750A4 !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 600 !important;
    font-size: ${TYPE.caption}px !important;
    text-transform: none !important;
    border-radius: ${RADIUS.sm}px !important;
    height: 32px;
    min-width: 72px;
    padding: 0 12px !important;
    white-space: nowrap;
    border: 1.5px solid #C4B0E8 !important;
    &:hover { background: #EDE7F6 !important; }
  }
`;

// ─── Score Bar Inline ─────────────────────────────────────────────────────────
const InlineScoreWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

const InlineBar = styled.div`
  width: 60px;
  height: 6px;
  border-radius: ${RADIUS.pill}px;
  background: #EDE7F6;
  overflow: hidden;
`;

const InlineFill = styled.div`
  height: 100%;
  border-radius: ${RADIUS.pill}px;
  background: ${p =>
    p.val >= 75 ? '#2D7D46' :
    p.val >= 50 ? '#6750A4' :
    p.val >= 30 ? '#E65100' : '#B3261E'};
  width: ${p => p.val}%;
  transition: width 0.4s ease;
`;

const ScoreText = styled.span`
  font-family: ${FONT_BODY};
  font-weight: 700;
  font-size: ${TYPE.small}px;
  font-variant-numeric: tabular-nums;
  min-width: 40px;
  text-align: right;
  color: ${p =>
    p.val >= 75 ? '#2D7D46' :
    p.val >= 50 ? '#6750A4' :
    p.val >= 30 ? '#E65100' : '#B3261E'};
`;

// ─── Status Chip ──────────────────────────────────────────────────────────────
const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 24px;
  padding: 0 12px;
  border-radius: ${RADIUS.pill}px;
  font-family: ${FONT_BODY};
  font-weight: 700;
  font-size: ${TYPE.overline}px;
  letter-spacing: 0.06em;
  line-height: 1;
  white-space: nowrap;
  background: ${p => p.bg};
  color: ${p => p.fg};
`;

const StatusDot = styled.span`
  width: 6px; height: 6px;
  border-radius: 50%;
  background: ${p => p.color};
  display: inline-block;
  flex-shrink: 0;
`;

// ─── Empty State ──────────────────────────────────────────────────────────────
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-align: center;
  padding: 40px 16px;
`;

const emptyTitleSx = { fontFamily: FONT_BODY, fontSize: TYPE.cardTitle, fontWeight: 700, lineHeight: 1.25, color: '#C4B0E8' };
const emptyTextSx = { fontFamily: FONT_BODY, fontSize: TYPE.body, lineHeight: 1.5, color: COLORS.muted };

// ─── SortableHeaderCell component ─────────────────────────────────────────────
const SortableHeaderCell = ({ onClick, tooltip, children, active, align }) => (
  <Tooltip title={tooltip} placement="top" arrow>
    <SortableHeadCell onClick={onClick} active={active ? 1 : 0} align={align}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}>
        {children}
      </Box>
    </SortableHeadCell>
  </Tooltip>
);

// ─── Status styling helper ────────────────────────────────────────────────────
const rejectedStatuses = [
  'ScreeningTest_Failed', 'Recruiter_Rejected', 'ReScreeningTest_Failed',
  'HiringManager_Rejected', 'Rejected', 'Candidate_Declined', 'Candidate_Quit',
  'ReTestPassed_Rejected', 'TestPassed_Rejected'
];

const passedStatuses = ['Selected', 'ScreeningTest_Passed', 'ReScreeningTest_Passed', 'Round2_Suggested'];

const getStatusChip = (status) => {
  if (status === 'NewCandidate')           return { bg: '#FFF8E1', fg: '#E65100', dot: '#FFB300' };
  if (status === 'Interview_Rescheduled')  return { bg: '#E3F2FD', fg: '#1565C0', dot: '#1E88E5' };  // ← new
  if (rejectedStatuses.includes(status))  return { bg: '#FDECEA', fg: '#B3261E', dot: '#E53935' };
  if (passedStatuses.includes(status))    return { bg: '#E6F4EA', fg: '#2D7D46', dot: '#2D7D46' };
  return { bg: '#EDE7F6', fg: '#4F378B', dot: '#7C5CBF' };
};

const DIFFS = ['easy', 'medium', 'hard'];

const allocate = (total, pct) => {
  const n = Number(total) || 0;
  if (n <= 0) return { easy: 0, medium: 0, hard: 0 };
  const raw = DIFFS.map(d => ({ d, v: (n * (Number(pct?.[d]) || 0)) / 100 }));
  const out = {};
  let assigned = 0;
  raw.forEach(r => { out[r.d] = Math.floor(r.v); assigned += out[r.d]; });
  [...raw].sort((a, b) => (b.v % 1) - (a.v % 1)).forEach(r => {
    if (assigned < n) { out[r.d] += 1; assigned += 1; }
  });
  if (assigned < n) out.medium += n - assigned;
  return out;
};

// A section is skipped when its question count is left empty (or 0).
const toCount = (v) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
};
 
const SECTIONS = [
  ['mcq',        'MCQ'],
  ['subjective', 'Subjective'],
  ['coding',     'Coding'],
];


// Logged-in user, used when the page is opened directly via /CandidateProfiles/:jdId.
const readStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('rbotUser')) || {};
  } catch {
    return {};
  }
};

// ─── Main Component ───────────────────────────────────────────────────────────
const CandidateProfiles = ({
  jdId: jdIdProp, userType: userTypeProp, candidateId, jobDescription, salaryRange,
  companyName, recruiterEmail: recruiterEmailProp, skills, experience, readOnly, onBack
}) => {
  const params = useParams();
  const [storedUser] = useState(readStoredUser);
  const jdId = jdIdProp || params.jdId;
  const userType = userTypeProp || storedUser.userType;
  const recruiterEmail = recruiterEmailProp || storedUser.email;

  const [profiles, setProfiles] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const navigate = useNavigate();
  const handleBack = () => (onBack ? onBack() : navigate(-1));
  // Mounted inside a dashboard whenever onBack is passed; the bare route has none.
  const embedded = Boolean(onBack);

  const [pendingStatus, setPendingStatus] = useState(0);
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCandidateData, setShowCandidateData] = useState(false);
  const [candidateEmail, setCandidateEmail] = useState('');
  const [overallStatus, setOverallStatus] = useState('');
  const [openDialog, setOpenDialog] = React.useState(false);
  // Starts true so the first render shows a loader, not the "no profiles" empty state.
  const [loading, setLoading] = useState(Boolean(jdId));
  const [loadError, setLoadError] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [configError, setConfigError] = useState('');
  const [resumeLoadingId, setResumeLoadingId] = useState(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [error, setError] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [pendingOnly, setPendingOnly] = useState(false);
  const fetchSeqRef = useRef(0);
  const [groupByStatus, setGroupByStatus] = useState(false);
  const [matchScoreSort, setMatchScoreSort] = useState('none');

  const baseUrl = process.env.REACT_APP_CANDIDATE_PROFILE_BY_JDID_API;
  const candidateProfileByJDApi = `${baseUrl}/${jdId}`;
  const getpdfApiUrl = process.env.REACT_APP_GET_PDF_API;
  const getrecruiterInterviewerApiUrl = process.env.REACT_APP_GET_RECRUITERS_INTERVIEWERS_API;
  const updatecandidateFieldsApiUrl = process.env.REACT_APP_UPDATE_CANDIDATE_FIELDS_API;
  const updateScreeningTestConfApiUrl = process.env.REACT_APP_UPDATE_SCREENING_CONFIGURATIONS_API;
  const getScreeningConfigApiUrl = process.env.REACT_APP_GET_SCREENING_CONFIG_API;

  const [showConfigureForm, setShowConfigureForm] = useState(false);
  const [formValues, setFormValues] = useState({ mcq: '', coding: '', screeningTime: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [interviewId, setInterviewId] = useState('');
  const [difficulty, setDifficulty]     = useState({ easy: 40, medium: 40, hard: 20 });
const [bank, setBank]                 = useState(null);
const [generatedMix, setGeneratedMix] = useState(null);

const diffTotal = DIFFS.reduce((s, d) => s + (Number(difficulty[d]) || 0), 0);

const sectionCounts  = {
  mcq:        toCount(formValues.mcq),
  subjective: toCount(formValues.subjective),
  coding:     toCount(formValues.coding),
};
const hasAnySection  = SECTIONS.some(([key]) => sectionCounts[key] > 0);


  const handlePrevPage = () => setCurrentPage((p) => Math.max(p - 1, 1));
  const handleNextPage = () => setCurrentPage((p) => Math.min(p + 1, totalPages));

  const handleMatchScoreSort = () => {
    setMatchScoreSort(prev => prev === 'none' ? 'desc' : prev === 'desc' ? 'asc' : 'none');
    setCurrentPage(1);
  };

  const formatStatus = (status = "") => {
  return status
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Za-z])(\d+)/g, "$1 $2")
    .replace(/\b\w/g, c => c.toUpperCase());
};

  const handleStatusGroupToggle = () => { setGroupByStatus(g => !g); setCurrentPage(1); };

  const endStatuses = ['Candidate_Declined', 'Candidate_Quit'];

  // Statuses awaiting action from the current role (mirrors backend getCandidates).
  const myEmail = String(recruiterEmail || '').toLowerCase();
  const isPendingForMe = (p) => {
    if (userType === 'recruiter') {
      return ['HiringManager_Accepted', 'ScreeningTest_Passed', 'NewCandidate', 'Round2_Suggested', 'ReScreeningTest_Passed'].includes(p.overallStatus);
    }
    if (userType === 'hiringManager') return ['Recruiter_Accepted', 'Interviewer_Selected'].includes(p.overallStatus);
    if (userType === 'interviewer') {
      if (!['Interview_Scheduled', 'Round2_Scheduled'].includes(p.overallStatus)) return false;
      // When interviews are populated, only count the ones this interviewer is on.
      const ivs = (Array.isArray(p.interviewid) ? p.interviewid : [p.interviewid]).filter(iv => iv && typeof iv === 'object');
      const latest = ivs[ivs.length - 1];
      if (!myEmail || !Array.isArray(latest?.interviewer)) return true;
      return latest.interviewer.some(e => String(e || '').toLowerCase() === myEmail);
    }
    return false;
  };

  const filteredProfiles = profiles.filter(profile => {
    if (pendingOnly && !isPendingForMe(profile)) return false;
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    return profile.candidatename?.toLowerCase().includes(q);
  });

  const sortedProfiles = filteredProfiles.slice().sort((a, b) => {
    const isEndA = endStatuses.includes(a.overallStatus);
    const isEndB = endStatuses.includes(b.overallStatus);
    if (isEndA && !isEndB) return 1;
    if (!isEndA && isEndB) return -1;
    if (matchScoreSort !== 'none') {
      const scoreA = a.match_score || 0;
      const scoreB = b.match_score || 0;
      return matchScoreSort === 'desc' ? scoreB - scoreA : scoreA - scoreB;
    }
    if (groupByStatus) {
      const statusCmp = (a.overallStatus || '').localeCompare(b.overallStatus || '');
      if (statusCmp !== 0) return statusCmp;
    }
    return new Date(b.uploadedAt) - new Date(a.uploadedAt);
  });

  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentProfiles = sortedProfiles.slice(startIndex, startIndex + itemsPerPage);
  const totalPages = Math.ceil(filteredProfiles.length / itemsPerPage);

  // Keep the current page in range when the list shrinks (refetch, search, etc.).
  useEffect(() => {
    const lastPage = Math.max(totalPages, 1);
    if (currentPage > lastPage) setCurrentPage(lastPage);
  }, [currentPage, totalPages]);

  // ─── Computed Stats ──────────────────────────────────────────────────────────
  const totalCount    = profiles.length;
  const passedCount   = profiles.filter(p => passedStatuses.includes(p.overallStatus)).length;
  const rejectedCount = profiles.filter(p => rejectedStatuses.includes(p.overallStatus)).length;
  const pendingCount  = profiles.filter(p =>
    !passedStatuses.includes(p.overallStatus) && !rejectedStatuses.includes(p.overallStatus)
  ).length;
  const avgScore = totalCount > 0
    ? Math.round(profiles.reduce((sum, p) => sum + (p.match_score || 0), 0) / totalCount)
    : 0;
  const topScore = totalCount > 0
    ? Math.max(...profiles.map(p => p.match_score || 0))
    : 0;

  // Score distribution buckets
  const buckets = [
    { label: '0–30%',  color: '#E53935', count: profiles.filter(p => (p.match_score || 0) < 30).length },
    { label: '30–50%', color: '#E65100', count: profiles.filter(p => (p.match_score || 0) >= 30 && (p.match_score || 0) < 50).length },
    { label: '50–75%', color: '#6750A4', count: profiles.filter(p => (p.match_score || 0) >= 50 && (p.match_score || 0) < 75).length },
    { label: '75%+',   color: '#2D7D46', count: profiles.filter(p => (p.match_score || 0) >= 75).length },
  ];

  const { log } = useActivityLogger(recruiterEmail, userType);

  // background: refresh without the full-screen loading overlay (e.g. after an edit).
  const fetchProfiles = async ({ background = false } = {}) => {
  if (!jdId) { setLoading(false); return; }
  // Ignore responses from an older request (JD switched / refresh clicked again).
  const seq = ++fetchSeqRef.current;
  const isStale = () => seq !== fetchSeqRef.current;
  if (!background) setLoading(true);
  try {
    const response = await axios.get(candidateProfileByJDApi);
    if (isStale()) return;

    // Tolerate 200 [] , 200 {candidates: []} , or anything unexpected.
    const raw = Array.isArray(response.data)
      ? response.data
      : Array.isArray(response.data?.candidates)
      ? response.data.candidates
      : [];

    setLoadError('');
    if (raw.length === 0) {
      setProfiles([]);
      return;
    }

    // Fetch the recruiter/interviewer list ONCE, not once per candidate.
    let recruiterEmails = [];
    try {
      const r = await axios.get(getrecruiterInterviewerApiUrl);
      recruiterEmails = r.data;
    } catch (e) {
      console.warn('[CandidateProfiles] recruiter list unavailable:', e.message);
    }
    if (isStale()) return;

    setProfiles(raw.map((profile) => ({ ...profile, recruiterEmails })));
  } catch (error) {
    if (isStale()) return;
    // 404 and 204 both mean "no candidates" in the current API — not a failure.
    const status = error.response?.status;
    if (status === 404 || status === 204) {
      setLoadError('');
      setProfiles([]);
      return;
    }

    // Log enough to tell a CORS/network failure from a real server error.
    console.error('[CandidateProfiles] fetch failed', {
      url: candidateProfileByJDApi,
      status: status ?? '(no response — CORS, network, or timeout)',
      code: error.code,
      message: error.message,
    });

    const message = status
      ? `Could not load candidates (server returned ${status}).`
      : 'Could not reach the server. Check your connection and try again.';
    // With no list on screen the error state (with Retry) shows the message;
    // on a failed refresh keep the current list and notify instead.
    setLoadError(message);
    if (profiles.length > 0) {
      setSnackbarMessage(message);
      setSnackbarOpen(true);
    }
  } finally {
    if (!background && !isStale()) setLoading(false);
  }
};

  useEffect(() => {
    if (jdId) fetchProfiles();
    else setLoading(false);
  }, [jdId, userType]);

  useEffect(() => {
    const count = profiles.filter(isPendingForMe).length;
    setPendingStatus(count);
    if (count === 0) setPendingOnly(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profiles, userType, myEmail]);

  const handleCandidateIdbutton = (candidateId, candidateEmail, overallStatus, interviewid) => {
    if (!candidateId) return;
    setSelectedCandidateId(candidateId);
    setIsModalOpen(true);
    setCandidateEmail(candidateEmail);
    setOverallStatus(overallStatus);
    setShowCandidateData(true);
    setInterviewId(interviewid);
  };

  // Background refresh: keep the list on screen instead of flashing the full loader.
  const handleClose = () => { setSelectedCandidateId(null); setShowCandidateData(false); fetchProfiles({ background: true }); };

  const [editingData, setEditingData] = React.useState({
    rowId: null, candidatename: '', candidateEmail: '', phone: '',
  });

  const handleEditClick = (candidateProfile) => {
    let extractedCountryCode = "";
    let extractedPhone = candidateProfile.phone || "";
    if (extractedPhone) {
      countryOptions.forEach((country) => {
        if (extractedPhone.startsWith(country.value)) {
          extractedCountryCode = country.value;
          extractedPhone = extractedPhone.replace(country.value, "").trim();
        }
      });
    }
    setEditingData({
      rowId: candidateProfile._id,
      candidatename: candidateProfile.candidatename || '',
      candidateEmail: candidateProfile.candidateEmail || '',
      phone: extractedPhone,
      countryCode: extractedCountryCode,
      totalExperience: candidateProfile.totalExperience || '',
      currentCTC: candidateProfile.currentCTC || '',
      expectedCTC: candidateProfile.expectedCTC || '',
      noticePeriod: candidateProfile.noticePeriod || '',
      presentLocation: candidateProfile.presentLocation || '',
      willingToRelocate: candidateProfile.willingToRelocate || '',
      preferredLocation: candidateProfile.preferredLocation || '',
    });
    setOpenDialog(true);
  };

  const handleDialogClose = () => { setOpenDialog(false); setResponseMessage(''); };
  const handleFieldChange = (field, value) => setEditingData(prev => ({ ...prev, [field]: value }));
  const handleInputChange = (e) => setFormValues(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleConfigureClick = async () => {
  if (configLoading) return;
  setBank(null);
  setGeneratedMix(null);
  setConfigError('');
  setConfigLoading(true);
  try {
    const { data } = await axios.get(`${getScreeningConfigApiUrl}/${jdId}`);
      const show = (v) => (v === undefined || v === null || Number(v) === 0 ? '' : v);
    // A JD that was never configured gets what candidates would receive today.
    const neverConfigured = [data.mcq, data.subjective, data.coding]
      .every(v => v === undefined || v === null);
    setFormValues({
      mcq:           neverConfigured ? 10 : show(data.mcq),
      subjective:    neverConfigured ? 5  : show(data.subjective),
      coding:        neverConfigured ? 3  : show(data.coding),
      screeningTime: data.screeningTime ?? '',
    });

    if (data.bank) setBank(data.bank);
    if (data.generatedMix) setGeneratedMix(data.generatedMix);
    if (data.difficulty) setDifficulty(data.difficulty);
    setShowConfigureForm(true);
  } catch (e) {
    // Don't open the form with stale/default values that could overwrite the saved config.
    console.warn('[CandidateProfiles] screening config unavailable:', e.message);
    const status = e.response?.status;
    setSnackbarMessage(
      status
        ? `Could not load the screening configuration (server returned ${status}). Please try again.`
        : 'Could not reach the server to load the screening configuration. Please try again.'
    );
    setSnackbarOpen(true);
  } finally {
    setConfigLoading(false);
  }
};

    const handleSubmit = async () => {
  if (configSaving) return;
  if (!hasAnySection) {
    setSnackbarMessage('Keep at least one section: enter a question count for MCQ, subjective or coding.');
    setSnackbarOpen(true);
    return;
  }
  if (!(Number(formValues.screeningTime) > 0)) {
    setSnackbarMessage('Enter the screening time in minutes.');
    setSnackbarOpen(true);
    return;
  }
  if (diffTotal !== 100) {
    setSnackbarMessage(`Difficulty percentages must add up to 100 (currently ${diffTotal}%).`);
    setSnackbarOpen(true);
    return;
  }
  setConfigError('');
  setConfigSaving(true);
  try {
    const response = await fetch(`${updateScreeningTestConfApiUrl}/${jdId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // sectionCounts last: empty fields go out as an explicit 0 (= skip this section)
      body: JSON.stringify({ ...formValues, ...sectionCounts, difficulty }),
    });
    const data = await response.json().catch(() => ({}));
 
    if (!response.ok) {
      setConfigError(data.message || `Failed to configure questions (server returned ${response.status}).`);
      return;
    }
 
    const skipped = SECTIONS.filter(([key]) => sectionCounts[key] === 0).map(([, label]) => label);
    setSnackbarMessage(
      (data.warnings?.length
        ? `Saved. ${data.warnings.length} difficulty bucket(s) will be topped up from other levels.`
        : 'Questions configured successfully')
      + (skipped.length ? ` ${skipped.join(' and ')} will be skipped in the test.` : '')
    );
    setSnackbarOpen(true);
    setShowConfigureForm(false);
    log('CONFIGURE_SCREENING', { jdId, metadata: { config: { ...formValues, ...sectionCounts }, difficulty } });
  } catch {
    setConfigError('Could not reach the server to save the configuration. Check your connection and try again.');
  } finally {
    setConfigSaving(false);
  }
};


  const handleResumedisplay = async (resumePaths, jdId, rowId) => {
    if (resumeLoadingId) return;
    const pathsArray = !resumePaths
      ? []
      : (Array.isArray(resumePaths) ? resumePaths.map(obj => obj?.path) : [resumePaths]).filter(Boolean);
    if (pathsArray.length === 0) {
      setSnackbarMessage('No resume on file for this candidate.');
      setSnackbarOpen(true);
      return;
    }
    // Open the tabs synchronously inside the click so popup blockers allow them,
    // then point them at the file once it has been fetched.
    const windows = pathsArray.map(() => window.open('', '_blank'));
    const closeWin = (win) => { if (win && !win.closed) win.close(); };
    setResumeLoadingId(rowId);
    try {
      for (let i = 0; i < pathsArray.length; i++) {
        const win = windows[i];
        const response = await axios.get(
          `${getpdfApiUrl}?fileName=${encodeURIComponent(pathsArray[i])}&jdId=${encodeURIComponent(jdId)}`,
          { responseType: 'arraybuffer' }
        );
        const contentType = (response.headers['content-type'] || '').split(';')[0].trim();
        const blob = new Blob([response.data], { type: contentType });
        const url = URL.createObjectURL(blob);
        if (contentType === 'application/pdf') {
          if (win && !win.closed) win.location.href = url;
          else window.open(url, '_blank');
        } else if (contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
          // .docx can't be shown in a tab; download it from this page instead.
          closeWin(win);
          window.location.href = url;
        } else {
          closeWin(win);
          setSnackbarMessage('Unsupported file type.');
          setSnackbarOpen(true);
        }
      }
    } catch {
      windows.forEach(closeWin);
      setSnackbarMessage('Failed to fetch resumes.');
      setSnackbarOpen(true);
    } finally {
      setResumeLoadingId(null);
    }
  };

  const handleEditCandidateData = async () => {
    if (!editingData.candidatename || !editingData.candidateEmail || !editingData.phone) {
      setSnackbarMessage('Candidate name, email, and phone are required.');
      setSnackbarOpen(true); setError(true); return;
    }
    if (editSaving) return;
    setEditSaving(true);
    setResponseMessage('');
    try {
      const response = await axios.put(`${updatecandidateFieldsApiUrl}/${editingData.rowId}`, {
        candidatename: editingData.candidatename, candidateEmail: editingData.candidateEmail,
        phone: `${editingData.countryCode || ""}${editingData.phone || ""}`,
        totalExperience: editingData.totalExperience, currentCTC: editingData.currentCTC,
        expectedCTC: editingData.expectedCTC, noticePeriod: editingData.noticePeriod,
        presentLocation: editingData.presentLocation, willingToRelocate: editingData.willingToRelocate,
        preferredLocation: editingData.preferredLocation,
      });
      log('EDIT_CANDIDATE', {
        targetId:   editingData.rowId,
        targetName: editingData.candidatename,
        jdId,
        metadata: {
          fieldsChanged: Object.keys(editingData).filter(k => k !== 'rowId'),
        }
      });
      setResponseMessage(response.data?.message || 'Candidate updated successfully.');
      setError(false);
      await fetchProfiles({ background: true });
      // Leave the success message visible briefly; Save stays disabled until the dialog closes.
      setTimeout(() => { setEditSaving(false); handleDialogClose(); }, 1000);
    } catch {
      setResponseMessage('Error updating candidate. Please try again.');
      setError(true);
      setEditSaving(false);
    }
  };


  const handleExportToExcel = () => {
    log('EXPORT_EXCEL', {
      jdId,
      metadata: {
        totalCandidates: sortedProfiles.length,
        jobRole: profiles[0]?.jobRole,
        exportedAt: new Date().toISOString(),
      }
    });
    const exportData = sortedProfiles.map((profile, index) => {
  const breakdown = profile.score_breakdown || {};
  const llm = breakdown.llm_detail || {};

  return {
    '#': index + 1,
    'Candidate ID': profile.candidateID || '',
    'resumeUrl': profile.resumeUrl || '',
    'Name': profile.candidatename || '',
    'Email': profile.candidateEmail || '',
    'Phone': profile.phone || '',

    'Final Match Score (%)': profile.match_score || breakdown.final_score || 0,
    'TF-IDF Score': breakdown.tfidf ?? '',
    'Semantic Score': breakdown.semantic ?? '',
    'LLM Overall Score': breakdown.llm ?? '',
    'Experience Score': llm.experience_score ?? '',
    'Education Score': llm.education_score ?? '',
    'Grade': breakdown.grade ?? '',

    'Status': profile.overallStatus || '',
    'Total Experience': profile.totalExperience || '',
    // 'Current CTC': profile.currentCTC || '',
    // 'Expected CTC': profile.expectedCTC || '',
    // 'Notice Period': profile.noticePeriod || '',
    // 'Present Location': profile.presentLocation || '',
    // 'Preferred Location': profile.preferredLocation || '',
    // 'Willing to Relocate': profile.willingToRelocate || '',
    'Job Role': profile.jobRole || '',
    'JD ID': profile.jdID || '',
    // 'Uploaded On': profile.uploadedAt
    //   ? new Date(profile.uploadedAt).toLocaleDateString('en-GB', {
    //       day: '2-digit',
    //       month: 'short',
    //       year: 'numeric',
    //     })
    //   : '',
  };
});
  
    const worksheet = XLSX.utils.json_to_sheet(exportData);
  
    // Column widths
    worksheet['!cols'] = [
      { wch: 4 }, { wch: 16 }, { wch: 22 }, { wch: 28 }, { wch: 16 },
      { wch: 16 }, { wch: 26 }, { wch: 18 }, { wch: 14 }, { wch: 14 },
      { wch: 16 }, { wch: 20 }, { wch: 20 }, { wch: 18 }, { wch: 20 },
      { wch: 14 }, { wch: 14 },
    ];
  
    const workbook = XLSX.utils.book_new();
    // Excel sheet names: max 31 chars, no : \ / ? * [ ] (e.g. "Frontend/React" would throw).
    const sheetName = (profiles[0]?.jobRole || 'Candidates').replace(/[:\\/?*[\]]/g, '-').slice(0, 31) || 'Candidates';
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  
    const d = new Date();
    const localDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const fileName = `Candidates_${profiles[0]?.jdID || jdId}_${localDate}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  // ─── RENDER ──────────────────────────────────────────────────────────────────
  return (
    <ThemeProvider theme={recruiterTheme}>
    <PageWrapper $embedded={embedded}>
      {!showCandidateData && (
        <>
          {/* Header */}
          <HeaderBar>
            <HeaderLeft>
              <BackBtn onClick={handleBack} aria-label="Back"><ArrowBackIcon /></BackBtn>
              <Box sx={{ minWidth: 0 }}>
                <PageTitle>Candidate Profiles</PageTitle>
                <PageSubtitle
                  title={profiles[0]?.jobRole ? [profiles[0].jobRole, profiles[0]?.jdID, companyName].filter(Boolean).join(' · ') : undefined}
                >
                  {profiles[0]?.jobRole ? (
                    <>
                      <Box component="span" sx={{ fontWeight: 600, color: '#4F378B' }}>{profiles[0].jobRole}</Box>
                      {[profiles[0]?.jdID, companyName].filter(Boolean).map((part) => ` · ${part}`).join('')}
                    </>
                  ) : 'Reviewing applicants for this position'}
                </PageSubtitle>
              </Box>
            </HeaderLeft>

            <HeaderActions>
              {profiles.length > 0 && (
                <ExportBtn onClick={handleExportToExcel}>
                  Export to Excel
                </ExportBtn>
              )}
              {userType === 'recruiter' && profiles.length > 0 && (
                <ConfigBtn
                  onClick={handleConfigureClick}
                  disabled={configLoading}
                  startIcon={configLoading ? <CircularProgress size={16} thickness={5} sx={{ color: '#fff' }} /> : null}
                >
                  {configLoading ? 'Loading…' : 'Configure Questions'}
                </ConfigBtn>
              )}
            </HeaderActions>
          </HeaderBar>

          {/* ── Stat Cards ── */}
          {profiles.length > 0 && (
            <StatsRow>
              <StatCard gradient="linear-gradient(135deg, #6750A4, #4F378B)" shadow="rgba(103,80,164,0.30)">
                <StatNum>{totalCount}</StatNum>
                <StatLbl>Total Candidates</StatLbl>
                <StatIconWrap><PeopleAltIcon /></StatIconWrap>
              </StatCard>

              <StatCard gradient="linear-gradient(135deg, #E91E8C, #C2185B)" shadow="rgba(233,30,140,0.26)">
                <StatNum>{pendingStatus}</StatNum>
                <StatLbl>Awaiting Review</StatLbl>
                <StatIconWrap><HourglassTopIcon /></StatIconWrap>
              </StatCard>

              <StatCard gradient="linear-gradient(135deg, #2E7D32, #1B5E20)" shadow="rgba(46,125,50,0.26)">
                <StatNum>{passedCount}</StatNum>
                <StatLbl>Progressed</StatLbl>
                <StatIconWrap><CheckCircleOutlineIcon /></StatIconWrap>
              </StatCard>

              <StatCard gradient="linear-gradient(135deg, #B71C1C, #C62828)" shadow="rgba(183,28,28,0.24)">
                <StatNum>{rejectedCount}</StatNum>
                <StatLbl>Not Proceeding</StatLbl>
                <StatIconWrap><CancelOutlinedIcon /></StatIconWrap>
              </StatCard>

              <StatCard gradient="linear-gradient(135deg, #00897B, #00695C)" shadow="rgba(0,137,123,0.26)">
                <StatNum>{avgScore}%</StatNum>
                <StatLbl>Avg Match Score</StatLbl>
                <StatIconWrap><TrendingUpIcon /></StatIconWrap>
              </StatCard>

              <StatCard gradient="linear-gradient(135deg, #F57F17, #E65100)" shadow="rgba(245,127,23,0.26)">
                <StatNum>{topScore}%</StatNum>
                <StatLbl>Top Score</StatLbl>
                <StatIconWrap><EmojiEventsIcon /></StatIconWrap>
              </StatCard>
            </StatsRow>
          )}

          {/* ── Score Distribution Bar ── */}
          {/* {profiles.length > 0 && (
            <ScoreBarWrap>
              <ScoreBarCard>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <ScoreBarLabel>Match Score Distribution</ScoreBarLabel>
                  <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: 12, color: '#8B7DB5', fontWeight: 600 }}>
                    {totalCount} candidates
                  </Typography>
                </Box>
                <SegBar>
                  {buckets.map(b => (
                    <SegFill key={b.label} color={b.color} pct={totalCount > 0 ? (b.count / totalCount) * 100 : 0} />
                  ))}
                </SegBar>
                <SegLegendRow>
                  {buckets.map(b => (
                    <SegLegendItem key={b.label}>
                      <SegDot color={b.color} />
                      {b.label} <span style={{ color: '#6750A4', fontWeight: 700 }}>({b.count})</span>
                    </SegLegendItem>
                  ))}
                </SegLegendRow>
              </ScoreBarCard>
            </ScoreBarWrap>
          )} */}

          {/* ── Controls Row ── */}
          {(profiles.length > 0 || (pendingStatus > 0 && !readOnly)) && (
            <ControlsBar>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', minWidth: 0 }}>
                {profiles.length > 0 && (
                  <SearchField
                    variant="outlined"
                    size="small"
                    placeholder="Search by candidate name…"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: '#6750A4' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}

                {(searchQuery || pendingOnly) && (
                  <ResultPill>
                    {filteredProfiles.length} result{filteredProfiles.length !== 1 ? 's' : ''}
                  </ResultPill>
                )}
              </Box>

              {/* Pending badge (toggles the "awaiting review" filter) */}
              {pendingStatus > 0 && !readOnly && (
                <PendingBadge
                  role="button" tabIndex={0}
                  title={pendingOnly ? 'Show all candidates' : 'Show only candidates awaiting your review'}
                  onClick={() => { setPendingOnly(v => !v); setCurrentPage(1); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPendingOnly(v => !v); setCurrentPage(1); } }}
                  style={{ cursor: 'pointer', outline: pendingOnly ? '2px solid #E65100' : undefined }}>
                  <HourglassTopIcon sx={{ color: '#E65100', fontSize: 20 }} />
                  <PendingNum>{pendingStatus}</PendingNum>
                  <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: TYPE.small, color: '#BF360C', whiteSpace: 'nowrap' }}>
                    {pendingStatus === 1 ? 'candidate' : 'candidates'} awaiting review{pendingOnly ? ' · showing only these' : ''}
                  </Typography>
                </PendingBadge>
              )}
            </ControlsBar>
          )}
        </>
      )}

      {/* ── Configure Dialog ── */}
      <Dialog open={showConfigureForm} onClose={() => { if (!configSaving) { setShowConfigureForm(false); setConfigError(''); } }} maxWidth="sm" fullWidth
        PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={dialogTitleSx}>
          Configure Screening
        </DialogTitle>
        <DialogContent dividers sx={dialogContentSx}>
                    {SECTIONS.map(([key, label]) => (
            <TextField
              key={key} fullWidth type="number" label={`Number of ${label} Questions`} name={key}
              value={formValues[key]} onChange={handleInputChange}
              placeholder="Leave empty to skip this section"
              helperText={sectionCounts[key] === 0 ? 'Empty: this section will be skipped' : ' '}
              inputProps={{ min: 0 }}
              sx={{ mb: 1, ...inputSx }}
            />
          ))}
          <TextField fullWidth label="Screening Time (minutes)" name="screeningTime" value={formValues.screeningTime} onChange={handleInputChange} type="number"
            sx={inputSx} />
          <Box sx={{
            mt: 2, p: 2, borderRadius: `${RADIUS.md}px`,
            background: hasAnySection ? '#F7F3FC' : '#FDECEA',
            border: `1px solid ${hasAnySection ? '#E5DAF6' : '#F5C6C4'}`,
          }}>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, fontWeight: 600, lineHeight: 1.5,
                              color: hasAnySection ? '#4F378B' : '#B3261E' }}>
              {hasAnySection
                ? `Candidates will get: ${SECTIONS.map(([key, label]) =>
                    sectionCounts[key] > 0 ? `${label} (${sectionCounts[key]})` : `${label} skipped`).join(', ')}.`
                : 'Keep at least one section: enter a question count for MCQ, subjective or coding.'}
            </Typography>
          </Box>

            <Box sx={{ mt: 2, p: 2, borderRadius: `${RADIUS.md}px`, background: '#F7F3FC', border: '1px solid #E5DAF6' }}>
  <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.body, lineHeight: 1.25, color: '#2D1B5E', mb: 0.5 }}>
    Difficulty mix
  </Typography>

  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
    <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, lineHeight: 1.5, color: '#8B7DB5' }}>
      {generatedMix
        ? `Bank is ${generatedMix.easy}% easy / ${generatedMix.medium}% medium / ${generatedMix.hard}% hard.`
        : configLoading
        ? 'Reading generated bank…'
        : 'No generated question bank found for this JD yet.'}
    </Typography>
    {generatedMix && (
      <Button size="small" onClick={() => setDifficulty(generatedMix)}
        sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, fontWeight: 600, textTransform: 'none', color: '#6750A4', minWidth: 0, height: 32, px: 1.5, borderRadius: `${RADIUS.sm}px` }}>
        Match bank
      </Button>
    )}
  </Box>

  <Grid container spacing={2}>
    {DIFFS.map(d => (
      <Grid item xs={4} key={d}>
        <TextField
          fullWidth size="small" type="number"
          label={`${d[0].toUpperCase()}${d.slice(1)} %`}
          value={difficulty[d]}
          onChange={(e) => setDifficulty(p => ({
            ...p, [d]: e.target.value === '' ? '' : Number(e.target.value),
          }))}
          inputProps={{ min: 0, max: 100 }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: `${RADIUS.sm}px`, background: '#fff' } }}
        />
      </Grid>
    ))}
  </Grid>

  <Typography sx={{ mt: 1, fontFamily: FONT_BODY, fontSize: TYPE.caption, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                    color: diffTotal === 100 ? '#2D7D46' : '#B3261E' }}>
    Total: {diffTotal}%{diffTotal !== 100 && ' — must be exactly 100%'}
  </Typography>

  {bank && (
    <Box sx={{ mt: 2 }}>
            {SECTIONS.map(([key, label]) => {
        if (sectionCounts[key] === 0) {
          return (
            <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
              <Box sx={{ width: 80, fontFamily: FONT_BODY, fontSize: TYPE.caption, fontWeight: 700, color: '#4F378B' }}>
                {label}
              </Box>
              <Box sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, fontWeight: 600, color: '#8B7DB5' }}>
                Skipped
              </Box>
            </Box>
          );
        }
        const want = allocate(formValues[key], difficulty);
        return (
          <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, flexWrap: 'wrap' }}>
            <Box sx={{ width: 80, fontFamily: FONT_BODY, fontSize: TYPE.caption, fontWeight: 700, color: '#4F378B' }}>
              {label}
            </Box>
            {DIFFS.map(d => {
              const have  = bank[key]?.[d] ?? 0;
              const short = want[d] > have;
              return (
                <Box key={d} sx={{
                  display: 'inline-flex', alignItems: 'center', height: 24, px: 1, borderRadius: `${RADIUS.sm}px`, fontFamily: FONT_BODY,
                  fontSize: TYPE.overline, fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                  background: short ? '#FDECEA' : '#EDE7F6',
                  color: short ? '#B3261E' : '#4F378B',
                }}>
                  {d}: {want[d]}/{have}
                </Box>
              );
            })}
          </Box>
        );
      })}

      <Typography sx={{ mt: 1, fontFamily: FONT_BODY, fontSize: TYPE.overline, lineHeight: 1.5, color: '#8B7DB5' }}>
        Shown as needed/available. Red means the bank is short — those slots are filled from the nearest difficulty.
      </Typography>
    </Box>
  )}
</Box>
          {configError && (
            <Typography sx={{ mt: 2, fontFamily: FONT_BODY, fontSize: TYPE.small, lineHeight: 1.5, color: '#B3261E', fontWeight: 600 }}>
              {configError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={() => { setShowConfigureForm(false); setConfigError(''); }} disabled={configSaving} sx={{ ...dialogBtnSx, color: '#6750A4' }}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={configSaving || diffTotal !== 100 || !hasAnySection}
        startIcon={configSaving ? <CircularProgress size={16} thickness={5} color="inherit" /> : null}
        sx={dialogBtnSx}>
        {configSaving ? 'Saving…' : 'Save Configuration'}
      </Button>
        </DialogActions>
      </Dialog>

  {/* Full-screen overlay only for refreshes; the first load shows an inline loader below. */}
  {loading && profiles.length > 0 && (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.75)',
        backdropFilter: 'blur(3px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <CircularProgress
          size={45}
          thickness={4}
          sx={{
            color: '#6750A4',
          }}
        />

        <Typography
          sx={{
            fontFamily: FONT_BODY,
            fontSize: TYPE.body,
            fontWeight: 600,
            color: '#4F378B',
          }}
        >
          Loading candidates...
        </Typography>
      </Box>
    </Box>
  )}
      {/* ── Table ── */}
      {!showCandidateData && (
        <>
          {loading && profiles.length === 0 ? (
            <EmptyState>
              <CircularProgress size={40} thickness={4} sx={{ color: '#6750A4' }} />
              <Typography sx={{ ...emptyTextSx, fontWeight: 600, color: '#4F378B' }}>
                Loading candidates...
              </Typography>
            </EmptyState>
          ) : loadError && profiles.length === 0 ? (
            <EmptyState>
              <Typography sx={emptyTitleSx}>
                Couldn't load candidates
              </Typography>
              <Typography sx={{ ...emptyTextSx, color: COLORS.error }}>
                {loadError}
              </Typography>
              <ConfigBtn onClick={() => fetchProfiles()} disabled={loading}>
                Retry
              </ConfigBtn>
            </EmptyState>
          ) : currentProfiles.length === 0 ? (
            <EmptyState>
              <Typography sx={emptyTitleSx}>
                No candidates found
              </Typography>
              <Typography sx={emptyTextSx}>
                {searchQuery ? 'Try a different search term'
                  : pendingOnly ? 'No candidates are awaiting your review'
                  : 'No profiles have been uploaded for this JD yet'}
              </Typography>
            </EmptyState>
          ) : (
            profiles.length > 0 && (
              <StyledTableWrap>
                {/* Job Role strip */}
                {/* <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}> */}
                  {/* <Typography sx={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 16, color: '#4F378B' }}>
                    {profiles[0]?.jobRole} - {profiles[0]?.jdID}
                  </Typography> */}
                  {/* <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: 12, color: '#8B7DB5', fontWeight: 600 }}>
                    Showing {startIndex + 1}–{Math.min(startIndex + itemsPerPage, filteredProfiles.length)} of {filteredProfiles.length}
                  </Typography> */}
                {/* </Box> */}

                <TableCard>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <HeadCell>Candidate ID</HeadCell>
                          <HeadCell>Name / Email</HeadCell>
                          <HeadCell>Phone</HeadCell>
                          <HeadCell>Resume</HeadCell>

                          {/* Match Score — sortable */}
                          <SortableHeaderCell
                            onClick={handleMatchScoreSort}
                            active={matchScoreSort !== 'none'}
                            align="right"
                            tooltip={
                              matchScoreSort === 'none' ? 'Click to sort: Highest first'
                              : matchScoreSort === 'desc' ? 'Highest → Lowest. Click for Lowest first'
                              : 'Lowest → Highest. Click to clear'
                            }
                          >
                            Match Score
                            {matchScoreSort === 'none'  && <SwapVertIcon sx={{ fontSize: 16, opacity: 0.6 }} />}
                            {matchScoreSort === 'desc'  && <ArrowDownwardIcon sx={{ fontSize: 16 }} />}
                            {matchScoreSort === 'asc'   && <ArrowUpwardIcon sx={{ fontSize: 16 }} />}
                            {matchScoreSort !== 'none' && (
                              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', height: 20, fontSize: TYPE.overline, letterSpacing: 0, textTransform: 'none', background: '#D0BCFF', color: '#3a2070', borderRadius: '4px', px: '4px', fontWeight: 700 }}>
                                {matchScoreSort === 'desc' ? 'High→Low' : 'Low→High'}
                              </Box>
                            )}
                          </SortableHeaderCell>

                          {/* Status — groupable */}
                          <SortableHeaderCell
                            onClick={handleStatusGroupToggle}
                            active={groupByStatus}
                            tooltip={groupByStatus ? 'Grouped by status — click to restore date order' : 'Click to group by status'}
                          >
                            Status
                            {groupByStatus ? <GroupWorkIcon sx={{ fontSize: 16 }} /> : <SortByAlphaIcon sx={{ fontSize: 16, opacity: 0.6 }} />}
                            {groupByStatus && (
                              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', height: 20, fontSize: TYPE.overline, letterSpacing: 0, textTransform: 'none', background: '#D0BCFF', color: '#3a2070', borderRadius: '4px', px: '4px', fontWeight: 700 }}>
                                Grouped
                              </Box>
                            )}
                          </SortableHeaderCell>

                          <HeadCell align="right">Uploaded</HeadCell>
                          {userType === 'recruiter' && !readOnly && <HeadCell align="right">Edit</HeadCell>}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {currentProfiles.map((profile) => {
                          const chip = getStatusChip(profile.overallStatus);
                          const score = profile.match_score || 0;
                          return (
                            <BodyRow key={profile._id}>
                              <BodyCell>
                                <CandidateIdBtn onClick={() => handleCandidateIdbutton(profile._id, profile.candidateEmail, profile.overallStatus, profile.interviewid)}>
                                  {profile.candidateID}
                                </CandidateIdBtn>
                              </BodyCell>
                              <BodyCell>
                                <NameStack>
                                  <NameLine title={profile.candidatename || undefined}>{profile.candidatename}</NameLine>
                                  <EmailLine title={profile.candidateEmail || undefined}>{profile.candidateEmail}</EmailLine>
                                </NameStack>
                              </BodyCell>
                              <BodyCell sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{profile.phone}</BodyCell>
                              <BodyCell>
                                <ResumeBtn
                                  onClick={() => handleResumedisplay(profile.resumeUrl, jdId, profile._id)}
                                  disabled={Boolean(resumeLoadingId)}
                                >
                                  {resumeLoadingId === profile._id
                                    ? <CircularProgress size={14} thickness={5} sx={{ color: '#6750A4' }} />
                                    : '📄 View'}
                                </ResumeBtn>
                              </BodyCell>

                              {/* Score with inline bar */}
                              <BodyCell align="right">
                                <InlineScoreWrap>
                                  <InlineBar>
                                    <InlineFill val={score} />
                                  </InlineBar>
                                  <ScoreText val={score}>{score}%</ScoreText>
                                </InlineScoreWrap>
                              </BodyCell>

                              {/* Status chip */}
                              <BodyCell>
                                <StatusChip bg={chip.bg} fg={chip.fg}>
                                <StatusDot color={chip.dot} />
                                {formatStatus(profile.overallStatus)}
                              </StatusChip>
                              </BodyCell>

                              <BodyCell align="right" sx={{ color: '#8B7DB5 !important', fontSize: `${TYPE.caption}px !important`, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                                {profile.uploadedAt
                                  ? new Date(profile.uploadedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                                  : '—'}
                              </BodyCell>

                              {userType === 'recruiter' && !readOnly && (
                                <BodyCell align="right">
                                  <IconButton size="small" onClick={() => handleEditClick(profile)} aria-label="Edit candidate"
                                    sx={{ width: 32, height: 32, color: '#6750A4', borderRadius: `${RADIUS.sm}px`, '&:hover': { background: '#EDE7F6' } }}>
                                    <EditIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </BodyCell>
                              )}
                            </BodyRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  

                  {totalPages > 1 && (
                    // Pagination renders a <td>, so it needs its own table row to be valid DOM.
                    // display:block keeps the cell's flex layout spanning the full card width.
                    <Table sx={{ '&, & tbody, & tr': { display: 'block' } }}>
                      <TableBody>
                        <TableRow>
                          <Pagination
                            itemsPerPage={itemsPerPage} currentPage={currentPage}
                            totalPages={totalPages} handlePrevPage={handlePrevPage} handleNextPage={handleNextPage}
                          />
                        </TableRow>
                      </TableBody>
                    </Table>
                  )}
                </TableCard>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', mt: 1.5 }}>
  <Typography
    sx={{
      fontFamily: FONT_BODY,
      fontSize: TYPE.caption,
      color: COLORS.muted,
      fontWeight: 600,
      fontVariantNumeric: 'tabular-nums',
    }}
  >
    Showing {startIndex + 1}–{Math.min(startIndex + itemsPerPage, filteredProfiles.length)} of {filteredProfiles.length}
  </Typography>
</Box>
              </StyledTableWrap>
            )
          )}
        </>
      )}

      {/* ── Edit Candidate Dialog ── */}
      <Dialog open={openDialog} onClose={handleDialogClose}
        PaperProps={{ sx: { width: '42%', minWidth: 'min(560px, calc(100vw - 32px))', maxWidth: 'none', ...dialogPaperSx } }}>
        <DialogTitle sx={dialogTitleSx}>
          Edit Candidate Details
        </DialogTitle>
        <DialogContent sx={dialogContentSx}>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}><TextField label="Candidate Name" fullWidth value={editingData?.candidatename || ""} onChange={(e) => handleFieldChange("candidatename", e.target.value)} sx={inputSx} /></Grid>
            <Grid item xs={12}><TextField label="Candidate Email" fullWidth value={editingData?.candidateEmail || ""} onChange={(e) => handleFieldChange("candidateEmail", e.target.value)} sx={inputSx} /></Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel shrink>Code</InputLabel>
                <Select value={editingData?.countryCode || ""} onChange={(e) => handleFieldChange("countryCode", e.target.value)} displayEmpty sx={{ borderRadius: `${RADIUS.sm}px` }}>
                  <MenuItem value=""><em>Select</em></MenuItem>
                  {countryOptions.map((c) => (<MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={8}><TextField label="Phone" fullWidth value={editingData?.phone || ""} onChange={(e) => handleFieldChange("phone", e.target.value)} sx={inputSx} /></Grid>
            <Grid item xs={6}><TextField label="Current CTC" fullWidth type="number" value={editingData?.currentCTC || ""} onChange={(e) => handleFieldChange("currentCTC", e.target.value)} sx={inputSx} /></Grid>
            <Grid item xs={6}><TextField label="Expected CTC" fullWidth type="number" value={editingData?.expectedCTC || ""} onChange={(e) => handleFieldChange("expectedCTC", e.target.value)} sx={inputSx} /></Grid>
            <Grid item xs={12}><TextField label="Total Experience" fullWidth type="number" value={editingData?.totalExperience || ""} onChange={(e) => handleFieldChange("totalExperience", e.target.value)} sx={inputSx} /></Grid>
            <Grid item xs={12}><TextField label="Notice Period (days)" fullWidth value={editingData?.noticePeriod || ""} onChange={(e) => handleFieldChange("noticePeriod", e.target.value)} sx={inputSx} /></Grid>
            <Grid item xs={12}><TextField label="Present Location" fullWidth value={editingData?.presentLocation || ""} onChange={(e) => handleFieldChange("presentLocation", e.target.value)} sx={inputSx} /></Grid>
            <Grid item xs={12}><TextField label="Preferred Location" fullWidth value={editingData?.preferredLocation || ""} onChange={(e) => handleFieldChange("preferredLocation", e.target.value)} sx={inputSx} /></Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Willing to Relocate</InputLabel>
                <Select value={editingData?.willingToRelocate || ""} label="Willing to Relocate" onChange={(e) => handleFieldChange("willingToRelocate", e.target.value)} sx={{ borderRadius: `${RADIUS.sm}px` }}>
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          {responseMessage && (
            <Typography sx={{ mt: 2, fontFamily: FONT_BODY, fontSize: TYPE.small, lineHeight: 1.5, color: error ? '#B3261E' : '#2D7D46', fontWeight: 600 }}>
              {responseMessage}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={handleDialogClose} sx={{ ...dialogBtnSx, color: '#6750A4', border: '1.5px solid #C4B0E8' }}>
            Cancel
          </Button>
          <Button onClick={handleEditCandidateData} disabled={editSaving}
            startIcon={editSaving ? <CircularProgress size={16} thickness={5} sx={{ color: '#fff' }} /> : null}
            sx={{ ...dialogBtnSx, background: 'linear-gradient(135deg,#6750A4,#4F378B)', color: '#fff',
              '&:hover': { background: 'linear-gradient(135deg,#4F378B,#2D1B5E)' },
              '&.Mui-disabled': { color: '#fff', opacity: 0.7 } }}>
            {editSaving ? 'Updating…' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Candidate Detail View ── */}
      {showCandidateData && (
        <CandidateData
          candidateId={selectedCandidateId} userRole={userType} candidateEmail={candidateEmail}
          overallStatus={overallStatus} jdId={jdId} jobDescription={jobDescription}
          salaryRange={salaryRange} companyName={companyName} recruiterEmail={recruiterEmail}
          skills={skills} experience={experience} interviewid={interviewId}
          readOnly={readOnly} onBack={handleClose}
        />
      )}

      <NotificationDialog open={snackbarOpen} message={snackbarMessage} onClose={() => setSnackbarOpen(false)} />
    </PageWrapper>
    </ThemeProvider>
  );
};

export default CandidateProfiles;